import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Card } from '@atoms/Card';
import { authService } from '@services/authService';
import { geoService } from '@services/geoService';
import { storageService } from '@services/storageService';
import { useAuthStore } from '@store/authStore';
import { getErrorMessage } from '@services/api';
import { LocationMapModal } from '@molecules/ServiceAreaMap';
import { mediaUrl } from '@/utils/mediaUrl';

export function SettingsSection() {
  const { user, refreshUser } = useAuthStore();
  const logoRef = useRef<HTMLInputElement>(null);
  const coverRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState({
    name: user?.name ?? '',
    city: user?.city ?? '',
    district: user?.district ?? '',
    neighborhood: user?.neighborhood ?? '',
    address: user?.address ?? '',
    phone: user?.phone ?? '',
    bio: user?.bio ?? '',
    logo: user?.logo ?? '',
    coverImage: user?.coverImage ?? '',
    latitude: user?.latitude,
    longitude: user?.longitude,
  });
  const [passwords, setPasswords] = useState({ current: '', next: '' });
  const [showMap, setShowMap] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const { data: neighborhoods } = useQuery({
    queryKey: ['neighborhoods', profile.city, profile.district],
    queryFn: () => geoService.getNeighborhoods(profile.city, profile.district),
    enabled: profile.city.length > 1 && profile.district.length > 1,
  });

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name ?? '',
        city: user.city ?? '',
        district: user.district ?? '',
        neighborhood: user.neighborhood ?? '',
        address: user.address ?? '',
        phone: user.phone ?? '',
        bio: user.bio ?? '',
        logo: user.logo ?? '',
        coverImage: user.coverImage ?? '',
        latitude: user.latitude,
        longitude: user.longitude,
      });
    }
  }, [user]);

  const uploadMutation = useMutation({
    mutationFn: ({ file, target }: { file: File; target: 'logo' | 'coverImage' }) =>
      storageService.uploadImage(file).then((url) => ({ url, target })),
    onSuccess: ({ url, target }) => {
      setProfile((prev) => ({ ...prev, [target]: url }));
    },
  });

  const profileMutation = useMutation({
    mutationFn: async () => {
      await authService.updateProfile({
        name: profile.name,
        phone: profile.phone,
        bio: profile.bio,
        address: profile.address,
        logo: profile.logo || undefined,
        coverImage: profile.coverImage || undefined,
      });
      await geoService.updateLocation({
        city: profile.city,
        district: profile.district,
        neighborhood: profile.neighborhood,
        address: profile.address,
        latitude: profile.latitude,
        longitude: profile.longitude,
      });
    },
    onSuccess: async () => {
      await refreshUser();
      setMessage('Profil güncellendi');
    },
    onError: (err) => setMessage(getErrorMessage(err)),
  });

  const passwordMutation = useMutation({
    mutationFn: () => authService.changePassword(passwords.current, passwords.next),
    onSuccess: () => {
      setPasswords({ current: '', next: '' });
      setMessage('Şifre güncellendi');
    },
    onError: (err) => setMessage(getErrorMessage(err)),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Ayarlar</h1>
        <p className="mt-1 text-sm text-slate-500">Profil, görsel kimlik ve konum bilgileri</p>
      </div>
      {message && <p className="text-sm text-brand-600">{message}</p>}

      <Card className="card-elevated space-y-3">
        <h3 className="font-medium">Profil Bilgileri</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <Input label="Firma Adı" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} />
          <Input label="Telefon" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
          <Input label="İl" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value.toUpperCase() })} />
          <Input label="İlçe" value={profile.district} onChange={(e) => setProfile({ ...profile, district: e.target.value.toUpperCase() })} />
          <label className="block text-sm md:col-span-2">
            <span className="mb-1 block font-medium text-gray-700">Mahalle</span>
            {neighborhoods?.length ? (
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
                value={profile.neighborhood}
                onChange={(e) => setProfile({ ...profile, neighborhood: e.target.value })}
              >
                <option value="">Seçin</option>
                {neighborhoods.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            ) : (
              <Input label="" value={profile.neighborhood} onChange={(e) => setProfile({ ...profile, neighborhood: e.target.value })} />
            )}
          </label>
          <Input label="Adres" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} className="md:col-span-2" />
          <Input label="Hakkında" value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })} className="md:col-span-2" />
        </div>
        <div className="flex flex-wrap gap-4">
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Logo</p>
            {profile.logo && <img src={mediaUrl(profile.logo)} alt="" className="mb-2 h-16 w-16 rounded-lg object-cover" />}
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMutation.mutate({ file: f, target: 'logo' }); }} />
            <Button type="button" size="sm" variant="outline" loading={uploadMutation.isPending} onClick={() => logoRef.current?.click()}>Logo Yükle</Button>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">Kapak Görseli</p>
            {profile.coverImage && <img src={mediaUrl(profile.coverImage)} alt="" className="mb-2 h-20 w-40 rounded-lg object-cover" />}
            <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadMutation.mutate({ file: f, target: 'coverImage' }); }} />
            <Button type="button" size="sm" variant="outline" loading={uploadMutation.isPending} onClick={() => coverRef.current?.click()}>Kapak Yükle</Button>
          </div>
        </div>
        <div>
          <p className="mb-2 text-sm text-gray-600">
            Konum: {profile.latitude != null && profile.longitude != null
              ? `${profile.latitude.toFixed(5)}, ${profile.longitude.toFixed(5)}`
              : 'Seçilmedi'}
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => setShowMap(true)}>Haritadan Konum Seç</Button>
        </div>
        <Button loading={profileMutation.isPending} onClick={() => profileMutation.mutate()}>Kaydet</Button>
      </Card>

      <Card className="card-elevated space-y-3">
        <h3 className="font-medium">Şifre Değiştir</h3>
        <Input label="Mevcut Şifre" type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} />
        <Input label="Yeni Şifre" type="password" value={passwords.next} onChange={(e) => setPasswords({ ...passwords, next: e.target.value })} />
        <Button loading={passwordMutation.isPending} onClick={() => passwordMutation.mutate()}>Şifreyi Güncelle</Button>
      </Card>

      <LocationMapModal
        open={showMap}
        onClose={() => setShowMap(false)}
        latitude={profile.latitude}
        longitude={profile.longitude}
        onSelect={(lat, lng) => setProfile({ ...profile, latitude: lat, longitude: lng })}
      />
    </div>
  );
}
